import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Move, MoveVertical } from 'lucide-react';
import { applyHeadlineAnchorDrag } from '@/lib/layouts/applyHeadlineAnchorDrag';
import { snapHeadlineAnchor } from '@/lib/layouts/snapHeadlineAnchor';
import { createRafScheduler } from '@/lib/dom/createRafScheduler';
import type { SnapGuides, SnapTarget } from '@/lib/layers/computeSnap';

export interface LayoutAnchor {
  x: number;
  y: number;
}

interface Props {
  anchor: LayoutAnchor;
  width: number;
  axis?: 'xy' | 'y';
  testId?: string;
  onChange: (next: LayoutAnchor) => void;
}

const SNAP_PX = 8;
const DRAG_HEIGHT = 0.1;
const NO_GUIDES: SnapGuides = { x: null, y: null };
const GUIDE_OFFSET: Record<SnapTarget, string> = { start: '0%', center: '50%', end: '100%' };
const KEY_STEPS: Record<string, [number, number]> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** A visible move grip with the same drag snapping media layers use. */
export const LayoutAnchorEditor: React.FC<Props> = ({
  anchor,
  width,
  axis = 'xy',
  testId = 'layout-headline-drag',
  onChange,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [guides, setGuides] = useState<SnapGuides>(NO_GUIDES);
  const drag = useRef<{
    startX: number;
    startY: number;
    x: number;
    y: number;
    axis: 'xy' | 'y';
    width: number;
  } | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const patchSchedulerRef = useRef(createRafScheduler(
    (next: LayoutAnchor) => onChangeRef.current(next),
  ));

  const bar = axis === 'y';

  const onPointerMove = useCallback((event: PointerEvent) => {
    const d = drag.current;
    if (!d || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const next = applyHeadlineAnchorDrag(
      { x: d.x, y: d.y },
      (event.clientX - d.startX) / box.width,
      (event.clientY - d.startY) / box.height,
      { axis: d.axis },
    );
    const snapping = !event.ctrlKey && !event.metaKey;
    const snapped = snapHeadlineAnchor({
      anchor: next,
      width: d.width,
      height: DRAG_HEIGHT,
      thresholdX: snapping && d.axis !== 'y' ? SNAP_PX / box.width : 0,
      thresholdY: snapping ? SNAP_PX / box.height : 0,
    });
    setGuides(snapped.guides);
    patchSchedulerRef.current.schedule({ x: d.axis === 'y' ? d.x : snapped.x, y: snapped.y });
  }, []);

  const onPointerUp = useCallback((event: PointerEvent) => {
    drag.current = null;
    if (event.type === 'pointercancel') patchSchedulerRef.current.cancel();
    else patchSchedulerRef.current.flush();
    setGuides(NO_GUIDES);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
  }, [onPointerMove]);

  useEffect(() => {
    const scheduler = patchSchedulerRef.current;
    setGuides(NO_GUIDES);
    return () => {
      drag.current = null;
      scheduler.cancel();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [axis, width, onPointerMove, onPointerUp]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    drag.current = {
      startX: event.clientX,
      startY: event.clientY,
      x: anchor.x,
      y: anchor.y,
      axis,
      width,
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const direction = KEY_STEPS[event.key];
    if (!direction || (bar && direction[0] !== 0)) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 0.05 : 0.01;
    const next = applyHeadlineAnchorDrag(anchor, direction[0] * step, direction[1] * step, { axis });
    const snapped = snapHeadlineAnchor({
      anchor: next, width, height: DRAG_HEIGHT, thresholdX: 0, thresholdY: 0,
    });
    onChangeRef.current({ x: bar ? anchor.x : snapped.x, y: snapped.y });
  };

  return (
    <div ref={containerRef} className="absolute inset-0 z-20 pointer-events-none" data-layer-ui="">
      <div
        data-testid={testId}
        role="button"
        tabIndex={0}
        aria-label={t('projectEditor.headlinePosition.move')}
        title={t('projectEditor.headlinePosition.move')}
        className={`absolute pointer-events-auto touch-none rounded-md border border-transparent outline-none hover:border-primary/60 hover:bg-primary/10 focus-visible:border-primary/60 focus-visible:bg-primary/10 ${bar ? 'cursor-ns-resize' : 'cursor-move'}`}
        style={{
          left: bar ? '0%' : `${anchor.x * 100}%`,
          top: `${anchor.y * 100}%`,
          width: bar ? '100%' : `${width * 100}%`,
          height: `${DRAG_HEIGHT * 100}%`,
          transform: bar ? 'translateY(-50%)' : 'translate(-50%, -50%)',
        }}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
      >
        <span
          aria-hidden="true"
          data-testid={`${testId}-grip`}
          className="pointer-events-none absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md border border-border bg-background/90 text-foreground shadow-sm"
        >
          {bar ? <MoveVertical className="h-4 w-4" /> : <Move className="h-4 w-4" />}
        </span>
      </div>
      {guides.x && (
        <div
          data-testid={`snap-guide-x-${guides.x}`}
          className="absolute top-0 bottom-0 z-30 w-px bg-primary pointer-events-none"
          style={{ left: GUIDE_OFFSET[guides.x], marginLeft: -0.5 }}
        />
      )}
      {guides.y && (
        <div
          data-testid={`snap-guide-y-${guides.y}`}
          className="absolute left-0 right-0 z-30 h-px bg-primary pointer-events-none"
          style={{ top: GUIDE_OFFSET[guides.y], marginTop: -0.5 }}
        />
      )}
    </div>
  );
};

export default LayoutAnchorEditor;
